// Utility functions for game score recording and management
import { getApiBaseUrl } from './apiConfig';

const API_BASE_URL = getApiBaseUrl();

/**
 * Records a game score to the database
 * @param {Object} scoreData - The score data to record
 * @param {number} scoreData.userId - User ID
 * @param {number} scoreData.class - Class/grade level (4-5)
 * @param {string} scoreData.gameType - Type of game (e.g., 'coin-quest', 'algebra-quest')
 * @param {number} scoreData.score - Score points
 * @param {number} scoreData.correct - Number of correct answers
 * @param {number} scoreData.total - Total number of questions
 * @param {number} scoreData.level - Final level reached
 * @param {number} scoreData.sessionDuration - Time played in seconds
 * @returns {Promise<Object>} Response from the API
 */
export const recordGameScore = async (scoreData) => {
  try {
    console.log('Recording game score:', scoreData);
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const token = user.token || localStorage.getItem('token');
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/api/scores`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        game_type: scoreData.gameType,
        class_level: scoreData.class,
        score: scoreData.score,
        correct_answers: scoreData.correct,
        total_questions: scoreData.total,
        session_duration: scoreData.sessionDuration,
        user_id: scoreData.userId || user.id // Use provided userId or current user's ID
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to record score');
    }

    const result = await response.json();
    console.log('Score recorded successfully:', result);
    return result;
  } catch (error) {
    console.error('Error recording game score:', error);
    throw error;
  }
};

/**
 * Level-based game configuration
 * Each game will have 5-10 levels with increasing difficulty
 */
export const GAME_LEVELS = {
  // Easy games: 5 levels
  'coin-quest': {
    maxLevel: 5,
    questionsPerLevel: 5,
    timePerLevel: 60, // seconds
    difficultyIncrease: 1.2
  },
  'fraction-pizza': {
    maxLevel: 5,
    questionsPerLevel: 4,
    timePerLevel: 45,
    difficultyIncrease: 1.3
  },
  'geometry-builder': {
    maxLevel: 6,
    questionsPerLevel: 4,
    timePerLevel: 50,
    difficultyIncrease: 1.2
  },
  
  // Medium games: 7-8 levels
  'algebra-quest': {
    maxLevel: 7,
    questionsPerLevel: 5,
    timePerLevel: 70,
    difficultyIncrease: 1.4
  },
  'angle-adventure': {
    maxLevel: 7,
    questionsPerLevel: 4,
    timePerLevel: 60,
    difficultyIncrease: 1.3
  },
  'pattern-master': {
    maxLevel: 8,
    questionsPerLevel: 5,
    timePerLevel: 55,
    difficultyIncrease: 1.2
  },
  'percentage-power': {
    maxLevel: 8,
    questionsPerLevel: 4,
    timePerLevel: 65,
    difficultyIncrease: 1.3
  },
  
  // Hard games: 10 levels
  'time-travel': {
    maxLevel: 10,
    questionsPerLevel: 3,
    timePerLevel: 80,
    difficultyIncrease: 1.5
  },
  'volume-vault': {
    maxLevel: 10,
    questionsPerLevel: 3,
    timePerLevel: 75,
    difficultyIncrease: 1.4
  },
  'word-problem-adventure': {
    maxLevel: 10,
    questionsPerLevel: 3,
    timePerLevel: 90,
    difficultyIncrease: 1.3
  }
};

/**
 * Checks if a game is complete (reached max level)
 * @param {string} gameType - Type of game
 * @param {number} currentLevel - Current level
 * @returns {boolean} True if game is complete
 */
export const isGameComplete = (gameType, currentLevel) => {
  const config = GAME_LEVELS[gameType];
  if (!config) return false;
  return currentLevel >= config.maxLevel;
};

/**
 * Gets the total questions needed to complete a game
 * @param {string} gameType - Type of game
 * @returns {number} Total questions for the game
 */
export const getTotalGameQuestions = (gameType) => {
  const config = GAME_LEVELS[gameType];
  if (!config) return 20; // Default fallback
  return config.maxLevel * config.questionsPerLevel;
};

/**
 * Calculates final score based on performance
 * @param {number} correctAnswers - Number of correct answers
 * @param {number} totalQuestions - Total questions answered
 * @param {number} level - Final level reached
 * @param {number} timeBonus - Time bonus points
 * @returns {number} Final score
 */
export const calculateFinalScore = (correctAnswers, totalQuestions, level, timeBonus = 0) => {
  const accuracy = totalQuestions > 0 ? (correctAnswers / totalQuestions) : 0;
  const baseScore = correctAnswers * 10; // 10 points per correct answer
  const levelBonus = level * 50; // 50 points per level completed
  const accuracyBonus = Math.round(accuracy * 100); // Accuracy percentage as bonus
  
  return baseScore + levelBonus + accuracyBonus + timeBonus;
};

/**
 * Gets the current user from localStorage
 * @returns {Object|null} User object or null if not logged in
 */
export const getCurrentUser = () => {
  try {
    const savedUser = localStorage.getItem('user');
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      const userData = JSON.parse(savedUser);
      if (userData && userData.id && userData.id !== -1) {
        return userData;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Calculates score percentage
 * @param {number} correct - Number of correct answers
 * @param {number} total - Total number of questions
 * @returns {number} Score percentage (0-100)
 */
export const calculateScore = (correct, total) => {
  if (total === 0) return 0;
  return Math.round((correct / total) * 100);
};

/**
 * Fetches questions for a specific game from the API
 * @param {number} classLevel - Class level (4 or 5)
 * @param {string} gameType - Type of game (e.g., 'multiplication-monsters')
 * @param {number} count - Number of questions to fetch (default: 10)
 * @returns {Promise<Array>} Array of question objects
 */
export const fetchGameQuestions = async (classLevel, gameType, count = 10) => {
  try {
    console.log(`Fetching ${count} questions for ${gameType} (Class ${classLevel})`);
    
    const response = await fetch(`http://localhost:5000/api/game-questions?class=${classLevel}&gameType=${gameType}&count=${count}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch questions: ${response.status}`);
    }
    
    const questions = await response.json();
    console.log(`Received ${questions.length} questions for ${gameType}`);
    return questions;
  } catch (error) {
    console.error('Error fetching game questions:', error);
    // Return fallback questions if API fails
    return generateFallbackQuestions(classLevel, gameType, count);
  }
};

/**
 * Generates fallback questions if API fails
 * @param {number} classLevel - Class level
 * @param {string} gameType - Type of game
 * @param {number} count - Number of questions
 * @returns {Array} Array of fallback questions
 */
const generateFallbackQuestions = (classLevel, gameType, count) => {
  const questions = [];
  
  for (let i = 0; i < count; i++) {
    if (classLevel === 4 && gameType === 'multiplication-monsters') {
      const num1 = Math.floor(Math.random() * 20) + 10;
      const num2 = Math.floor(Math.random() * 20) + 10;
      const correct = num1 * num2;
      
      questions.push({
        id: i + 1,
        question: `What is ${num1} × ${num2}?`,
        choices: [
          correct.toString(),
          (correct + Math.floor(Math.random() * 50) + 10).toString(),
          (correct - Math.floor(Math.random() * 50) - 10).toString(),
          (correct + Math.floor(Math.random() * 100) + 20).toString()
        ],
        answer: 0,
        points: 10
      });
    } else {
      // Generic fallback
      const a = Math.floor(Math.random() * 50) + 10;
      const b = Math.floor(Math.random() * 50) + 10;
      const correct = a + b;
      
      questions.push({
        id: i + 1,
        question: `What is ${a} + ${b}?`,
        choices: [
          correct.toString(),
          (correct + 5).toString(),
          (correct - 5).toString(),
          (correct + 10).toString()
        ],
        answer: 0,
        points: 10
      });
    }
  }
  
  return questions;
};

/**
 * Determines class level based on game difficulty or user selection
 * @param {string} gameName - Name of the game
 * @param {number} difficulty - Difficulty level (optional)
 * @returns {number} Class level (1-5)
 */
export const determineClassLevel = (gameName, difficulty = 1) => {
  // Map games to appropriate class levels
  const gameClassMap = {
    'MultiplicationMonsters': 4,
    'DivisionDash': 4,
    'FractionPizza': 5,
    'AlgebraQuest': 5,
    'GeometryBuilder': 5,
    'DecimalDragons': 4,
    'PatternMaster': 3,
    'NumberShooting': 3,
    'WordProblemAdventure': 4,
    'AngleAdventure': 5,
    'PercentagePower': 5,
    'VolumeVault': 5,
    'TreasureHuntMath': 5,
    'MathShooterPro': 5,
    'SpeedRacerMath': 4,
    'MathNinjaWarrior': 4,
    'RocketMathRace': 5,
    'TimeTravel': 3,
    'CoinQuest': 3,
    'WordPuzzleMaster': 2,
    'MemoryMatchMath': 2,
    'PuzzleUnlock': 2,
    'DragDropMatch': 1,
    'NumberRunner': 1
  };

  return gameClassMap[gameName] || difficulty || 3;
};

/**
 * Maps game IDs to their backend game type identifiers
 * @param {string} gameId - Game ID from the route
 * @returns {string} Backend game type identifier
 */
export const getGameTypeFromId = (gameId) => {
  const gameTypeMap = {
    'multiplication-monsters': 'multiplication-monsters',
    'division-dash': 'division-dash',
    'fraction-pizza': 'fraction-pizza',
    'decimal-dragons': 'decimal-dragons',
    'geometry-builder': 'geometry-builder',
    'coin-quest': 'coin-quest',
    'time-travel': 'time-travel',
    'pattern-master': 'pattern-master',
    'number-shooting': 'number-shooting',
    'word-problem-adventure': 'word-problem-adventure',
    'angle-adventure': 'angle-adventure',
    'percentage-power': 'percentage-power',
    'algebra-quest': 'algebra-quest',
    'volume-vault': 'volume-vault',
    'treasure-hunt-math': 'treasure-hunt-math',
    'math-shooter-pro': 'math-shooter-pro',
    'speed-racer-math': 'speed-racer-math',
    'math-ninja-warrior': 'math-ninja-warrior',
    'rocket-math-race': 'rocket-math-race'
  };
  
  return gameTypeMap[gameId] || gameId;
};

/**
 * Shows a success message when score is recorded
 * @param {number} score - The score achieved
 * @param {number} correct - Number of correct answers
 * @param {number} total - Total questions
 */
export const showScoreRecordedMessage = (score, correct, total) => {
  console.log(`🎉 Score recorded! ${correct}/${total} correct (${score}%)`);
  // You can add toast notifications or other UI feedback here
};